import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { hideLoader, showLoader } from '../../Store/mainSlice';
import httpClient from '../../_util/api';
import CustomizedSnackbars from '../../shared/Snackbar/SnackBar';

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  gender: '',
  address: '',
  town: '',
  country: '',
  phoneNo_Mobile: '',
  phoneNo_Home: '',
  presentHouseholdIncome: '',
  totalFamilyMembers: '',
  fatherName: '',
  motherName: '',
  orphan: '',
  signature: '',
  referenceName1: '',
  referenceEmail1: '',
  referencePhone1: '',
  referenceName2: '',
  referenceEmail2: '',
  referencePhone2: '',
};

const Section = ({ title, children }) => (
  <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3, mb: 3 }}>
    <Typography variant="subtitle1" fontWeight="bold" color="#208146" mb={2}>
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Paper>
);

const Row = ({ children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
    {children}
  </Stack>
);

const StudentProfile = () => {
  const dispatch = useDispatch();
  const snackbarRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [readOnly, setReadOnly] = useState({ email: '', CNICNo: '' });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const loadProfile = async () => {
    dispatch(showLoader());
    try {
      const res = await httpClient.get('/students/my-profile');
      if (res.status === 200) {
        const s = res.data.student;
        setReadOnly({ email: s.email || '', CNICNo: s.CNICNo || '' });
        setForm({
          firstName: s.firstName || '',
          lastName: s.lastName || '',
          gender: GENDER_OPTIONS.find(g => g.toLowerCase() === (s.gender || '').toLowerCase()) || '',
          address: s.address || '',
          town: s.town || '',
          country: s.country || '',
          phoneNo_Mobile: s.phoneNo_Mobile || '',
          phoneNo_Home: s.phoneNo_Home || '',
          presentHouseholdIncome: s.presentHouseholdIncome ?? '',
          totalFamilyMembers: s.totalFamilyMembers ?? '',
          fatherName: s.fatherName || '',
          motherName: s.motherName || '',
          orphan: s.orphan || '',
          signature: s.signature || '',
          referenceName1: s.referenceName1 || '',
          referenceEmail1: s.referenceEmail1 || '',
          referencePhone1: s.referencePhone1 || '',
          referenceName2: s.referenceName2 || '',
          referenceEmail2: s.referenceEmail2 || '',
          referencePhone2: s.referencePhone2 || '',
        });
      }
    } catch (error) {
      snackbarRef.current?.showMessage(
        'error',
        error?.response?.data?.message || 'Failed to load profile.',
        '',
        'i-chk-circle'
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSubmit = async () => {
    dispatch(showLoader());
    try {
      const res = await httpClient.put('/students/my-profile', form);
      if (res.status === 200) {
        snackbarRef.current?.showMessage('success', res.data.message, '', 'i-chk-circle');
      }
    } catch (error) {
      snackbarRef.current?.showMessage(
        'error',
        error?.response?.data?.message || 'Update failed. Please try again.',
        '',
        'i-chk-circle'
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <CustomizedSnackbars ref={snackbarRef} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          My Profile
        </Typography>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ bgcolor: '#208146', ':hover': { bgcolor: '#176337' } }}
        >
          Save Changes
        </Button>
      </Stack>

      <Section title="Account Information">
        <Row>
          <TextField label="Email" value={readOnly.email} InputProps={{ readOnly: true }} fullWidth disabled />
          <TextField label="CNIC Number" value={readOnly.CNICNo} InputProps={{ readOnly: true }} fullWidth disabled />
        </Row>
      </Section>

      <Section title="Personal Information">
        <Row>
          <TextField
            label="First Name"
            value={form.firstName}
            onChange={handleChange('firstName')}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={form.lastName}
            onChange={handleChange('lastName')}
            fullWidth
          />
        </Row>
        <Row>
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select label="Gender" value={form.gender} onChange={handleChange('gender')}>
              {GENDER_OPTIONS.map((g) => (
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Signature"
            value={form.signature}
            onChange={handleChange('signature')}
            fullWidth
          />
        </Row>
        <Row>
          <TextField
            label="Mobile Phone"
            value={form.phoneNo_Mobile}
            onChange={handleChange('phoneNo_Mobile')}
            fullWidth
          />
          <TextField
            label="Home Phone"
            value={form.phoneNo_Home}
            onChange={handleChange('phoneNo_Home')}
            fullWidth
          />
        </Row>
      </Section>

      <Section title="Address">
        <Row>
          <TextField
            label="Address"
            value={form.address}
            onChange={handleChange('address')}
            fullWidth
          />
          <TextField
            label="Town / City"
            value={form.town}
            onChange={handleChange('town')}
            fullWidth
          />
        </Row>
        <Row>
          <TextField
            label="Country"
            value={form.country}
            onChange={handleChange('country')}
            fullWidth
          />
        </Row>
      </Section>

      <Section title="Family Information">
        <Row>
          <TextField
            label="Father's Name"
            value={form.fatherName}
            onChange={handleChange('fatherName')}
            fullWidth
          />
          <TextField
            label="Mother's Name"
            value={form.motherName}
            onChange={handleChange('motherName')}
            fullWidth
          />
        </Row>
        <Row>
          <TextField
            label="Gross Household Income (PKR)"
            type="number"
            value={form.presentHouseholdIncome}
            onChange={handleChange('presentHouseholdIncome')}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Please enter the total income of all earning members of the household, including the parent(s).">
                    <InfoOutlined sx={{ fontSize: 18, color: 'rgba(0, 0, 0, 0.54)', cursor: 'help' }} />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Total Family Members"
            type="number"
            value={form.totalFamilyMembers}
            onChange={handleChange('totalFamilyMembers')}
            fullWidth
          />
        </Row>
        <Row>
          <FormControl fullWidth>
            <InputLabel>Orphan</InputLabel>
            <Select label="Orphan" value={form.orphan} onChange={handleChange('orphan')}>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Row>
      </Section>

      <Section title="Reference 1">
        <Row>
          <TextField
            label="Name"
            value={form.referenceName1}
            onChange={handleChange('referenceName1')}
            fullWidth
          />
          <TextField
            label="Email"
            value={form.referenceEmail1}
            onChange={handleChange('referenceEmail1')}
            fullWidth
          />
        </Row>
        <Row>
          <TextField
            label="Phone"
            value={form.referencePhone1}
            onChange={handleChange('referencePhone1')}
            fullWidth
          />
        </Row>
      </Section>

      <Section title="Reference 2">
        <Row>
          <TextField
            label="Name"
            value={form.referenceName2}
            onChange={handleChange('referenceName2')}
            fullWidth
          />
          <TextField
            label="Email"
            value={form.referenceEmail2}
            onChange={handleChange('referenceEmail2')}
            fullWidth
          />
        </Row>
        <Row>
          <TextField
            label="Phone"
            value={form.referencePhone2}
            onChange={handleChange('referencePhone2')}
            fullWidth
          />
        </Row>
      </Section>

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ bgcolor: '#208146', ':hover': { bgcolor: '#176337' }, minWidth: 160 }}
        >
          Save Changes
        </Button>
      </Box>
    </Container>
  );
};

export default StudentProfile;
